import React from 'react'
import _ from 'lodash'

import LoginActions from '../../../../actions/LoginActions.js'
import {Alert, Badge, Button, Card, Divider, H3, H5, IconButton, P, S2, Tab, Tabs} from '../../../../components/UI/index.js'
import {FormStack, Input, Select, TextArea, Toggle, UploadImage} from '../../../../components/UI/forms/index.js'
import TrackTracePage from '../../../../trackTrace/TrackTracePage.js'
import {sampleStates, sampleView} from '../../../../trackTrace/sampleView.js'
import {defaultMessages, languageNames} from '../../../../trackTrace/i18n.js'
import {contrastWithWhite} from '../../../../trackTrace/theme.js'
import {trackTraceBranding} from '../../../../../utils/trackTraceBranding.js'
import {DEFAULT_DELIVERY_DAY_COUNT, DEFAULT_PICKUP_PERIOD, NEW_PAGE_FOR_ALL_FROM, RECIPIENT_MESSAGE_MAX, defaultSafePlaceOptions, newPageForAll, trackTraceLanguages, trackTracePhotoSizes} from '../../../../../types/trackTrace.js'
import t from 'src/helpers/Languages.js'

const messageGroups = [
    {title: 'ttsGroupPickup', fields: [
        {key: 'pickupNotCompletedMessage', label: 'ttsMessagePickupNotCompleted'},
        {key: 'pickupNotHomeMessage', label: 'ttsMessagePickupNotHome'},
        {key: 'pickupCancelledMessage', label: 'ttsMessagePickupCancelled'}
    ]},
    {title: 'ttsGroupNotCompleted', fields: [
        {key: 'deliveryNotCompletedRescheduleMessage', label: 'ttsMessageReschedule'},
        {key: 'deliveryNotCompletedRetourMessage', label: 'ttsMessageWrongAddress'},
        {key: 'deliveryNotCompletedRefusedMessage', label: 'ttsMessageRefused'},
        {key: 'deliveryNotCompletedNoFollowUpMessage', label: 'ttsMessageNoFollowUp'}
    ]},
    {title: 'ttsGroupNotHome', fields: [
        {key: 'deliveryNotHomeRescheduleMessage', label: 'ttsMessageReschedule'},
        {key: 'deliveryNotHomeOnHoldMessage', label: 'ttsMessageOnHold'},
        {key: 'deliveryNotHomeRetourMessage', label: 'ttsMessageRetour'},
        {key: 'deliveryNotHomeNoFollowUpMessage', label: 'ttsMessageNoFollowUp'}
    ]},
    {title: 'ttsGroupOther', fields: [
        {key: 'deliveryCancelledMessage', label: 'ttsMessageCancelled'},
        {key: 'deliveryMancoMessage', label: 'ttsMessageManco'}
    ]}
]

const sectionStyle = {display: 'flex', flexDirection: 'column', gap: 12, padding: 24}

// Width of the desktop preview before it is zoomed out to fit next to the settings
const DESKTOP_PREVIEW_WIDTH = 1200

class TrackTrace extends React.Component {
    constructor(props) {
        super(props)

        this.state = {
            tab: 'huisstijl',
            language: 'nl',
            previewDevice: 'mobile',
            previewState: 'onTheWay',
            previewSample: {choice: null, instructionsText: ''},
            newSafePlace: '',
            photoError: {desktop: '', mobile: ''},
            previewZoom: 0.4
        }

        this.desktopPreview = React.createRef()
    }

    componentDidMount() {
        // The desktop preview is zoomed out to the width of the preview panel
        this.resizeObserver = new ResizeObserver(([entry]) => {
            const previewZoom = Math.min(1, entry.contentRect.width / DESKTOP_PREVIEW_WIDTH)

            if (previewZoom > 0 && Math.abs(previewZoom - this.state.previewZoom) > 0.005) {
                this.setState({previewZoom})
            }
        })
    }

    componentDidUpdate() {
        if (this.desktopPreview.current && this.observed !== this.desktopPreview.current) {
            this.observed = this.desktopPreview.current
            this.resizeObserver.disconnect()
            this.resizeObserver.observe(this.observed)
        }
    }

    componentWillUnmount() {
        this.resizeObserver.disconnect()
    }

    settings() {
        return this.props.reseller.settings.orders.trackTrace
    }

    // Updates the value locally, the parent saves it when a path is given
    set(path, value, save = true) {
        const reseller = {...this.props.reseller}
        _.set(reseller.settings, path, value)
        this.props.onChange(reseller, save ? path : undefined)
    }

    save(path) {
        this.props.onChange({...this.props.reseller}, path)
    }

    setAction(action, value) {
        this.set('orders.trackTrace.actions', {...this.settings().actions, [action]: value})
    }

    enabledLanguages() {
        const languages = this.settings().languages
        return languages?.length ? languages : [...trackTraceLanguages]
    }

    onToggleLanguage(language, enabled) {
        const current = this.enabledLanguages()
        const languages = enabled ? trackTraceLanguages.filter((code) => code === language || current.includes(code)) : current.filter((code) => code !== language)

        // At least one language, and the default language stays available
        if (languages.length === 0) {
            return
        }

        this.set('orders.trackTrace.languages', languages)

        if (!languages.includes(this.settings().defaultLanguage || 'nl')) {
            this.set('orders.trackTrace.defaultLanguage', languages[0])
        }
    }

    messagePath(key) {
        const {language} = this.state
        return language === 'nl' ? `orders.trackTrace.${key}` : `orders.trackTrace.translations.${language}.${key}`
    }

    faq() {
        return this.settings().faq || []
    }

    onChangeFaq(index, field, value) {
        const faq = this.faq().map((item, i) => i === index ? {...item, [field]: value} : item)
        this.set('orders.trackTrace.faq', faq, false)
    }

    safePlaceOptions() {
        const options = this.settings().safePlaceOptions
        return options?.length ? options : [...defaultSafePlaceOptions]
    }

    addSafePlace() {
        const place = this.state.newSafePlace.trim()
        const options = this.safePlaceOptions()

        if (place && !options.some((option) => option.toLowerCase() === place.toLowerCase())) {
            this.set('orders.trackTrace.safePlaceOptions', [...options, place])
        }

        this.setState({newSafePlace: ''})
    }

    pickupPoints() {
        return this.settings().pickupPoints || []
    }

    onChangePickupPoint(index, field, value, save = false) {
        const pickupPoints = this.pickupPoints().map((point, i) => i === index ? {...point, [field]: value} : point)
        this.set('orders.trackTrace.pickupPoints', pickupPoints, save)
    }

    addPickupPoint() {
        const id = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`
        this.set('orders.trackTrace.pickupPoints', [...this.pickupPoints(), {id, name: '', address: '', openingHours: ''}], false)
    }

    // The photo is saved as a file on the server, which checks the minimum size
    onChangePhoto(kind, dataUrl) {
        this.setState({photoError: {...this.state.photoError, [kind]: ''}})

        LoginActions.setTrackTracePhoto(kind, dataUrl || null, (err, id) => {
            if (err) {
                this.setState({photoError: {...this.state.photoError, [kind]: err}})
                return
            }

            this.set(`orders.trackTrace.${kind === 'desktop' ? 'photoDesktop' : 'photoMobile'}`, id || undefined, false)
        })
    }

    renderSwitchover() {
        const settings = this.settings()

        // After the switchover date every reseller uses the new page, the switch is gone
        if (newPageForAll()) {
            return null
        }

        const info = [
            t(settings.newPage ? 'ttsNewPageOn' : 'ttsNewPageOff'),
            NEW_PAGE_FOR_ALL_FROM ? t('ttsSwitchoverDate', new Date(`${NEW_PAGE_FOR_ALL_FROM}T00:00:00`).toLocaleDateString('nl-NL', {day: 'numeric', month: 'long', year: 'numeric'})) : ''
        ].filter(Boolean).join(' ')

        return (
            <Toggle
                label={t('ttsUseNewPage')}
                info={info}
                checked={!!settings.newPage}
                onChange={(event) => this.set('orders.trackTrace.newPage', event.target.checked)}
            />
        )
    }

    renderHuisstijl() {
        const {reseller, history} = this.props
        const settings = this.settings()
        const color = reseller.settings.color || ''
        const contrast = contrastWithWhite(color)

        return (
            <>
                <H5>{t('ttsLogo')}</H5>
                <div style={{display: 'flex', gap: 12, alignItems: 'center'}}>
                    {reseller.settings.logo && <img src={reseller.settings.logo} alt={t('ttsCurrentLogo')} style={{maxHeight: 48, maxWidth: 160, objectFit: 'contain'}} />}
                    <S2>{t('ttsLogoInfo')}</S2>
                </div>
                <Button style={{alignSelf: 'flex-start'}} variant='outline' label={t('ttsCompanyDetails')} onClick={() => history.push('/instellingen/algemeen/bedrijfsgegevens')} />

                <Divider />

                <H5>{t('ttsMainColor')}</H5>
                <FormStack>
                    <Input
                        label={t('ttsColorHex')}
                        info={t('ttsColorInfo')}
                        value={color}
                        onChange={(event) => this.set('color', event.target.value.trim(), false)}
                        onBlur={() => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(color) && this.save('color')}
                    />
                </FormStack>
                {contrast > 0 &&
                    <Alert variant={contrast >= 4.5 ? 'success' : 'warning'}>
                        {t(contrast >= 4.5 ? 'ttsContrastOk' : 'ttsContrastLow', contrast.toFixed(1).replace('.', ','))}
                    </Alert>
                }

                <Divider />

                <H5>{t('ttsContact')}</H5>
                <FormStack>
                    {['phone', 'whatsapp', 'email', 'footerTitle'].map((field) => (
                        <Input
                            key={field}
                            label={t(`ttsField_${field}`)}
                            type={field === 'email' ? 'email' : field === 'footerTitle' ? 'text' : 'tel'}
                            value={settings[field] || ''}
                            onChange={(event) => this.set(`orders.trackTrace.${field}`, event.target.value, false)}
                            onBlur={() => this.save(`orders.trackTrace.${field}`)}
                        />
                    ))}
                    <TextArea
                        label={t('ttsFooterMessage')}
                        rows={3}
                        value={settings.footerMessage || ''}
                        onChange={(event) => this.set('orders.trackTrace.footerMessage', event.target.value, false)}
                        onBlur={() => this.save('orders.trackTrace.footerMessage')}
                    />
                    <Toggle
                        label={t('ttsHideAddress')}
                        info={t('ttsHideAddressInfo', reseller.settings.accountName || reseller.settings.companyAddress?.name || '')}
                        checked={!!settings.hideAddress}
                        onChange={(event) => this.set('orders.trackTrace.hideAddress', event.target.checked)}
                    />
                </FormStack>
            </>
        )
    }

    renderActions() {
        const settings = this.settings()
        const actions = settings.actions || {}
        const options = this.safePlaceOptions()
        const pickupPoints = this.pickupPoints()

        return (
            <>
                <H5>{t('ttsActionsTitle')}</H5>
                <S2>{t('ttsActionsInfo')}</S2>

                <FormStack>
                    <Toggle label={t('ttsSafePlace')} info={t('ttsSafePlaceInfo')} checked={!!actions.safePlace} onChange={(event) => this.setAction('safePlace', event.target.checked)} />
                    <Toggle label={t('ttsNeighbour')} info={t('ttsNeighbourInfo')} checked={!!actions.neighbour} onChange={(event) => this.setAction('neighbour', event.target.checked)} />
                    <Toggle label={t('ttsPickupPoint')} info={t('ttsPickupPointInfo')} checked={!!actions.pickupPoint} onChange={(event) => this.setAction('pickupPoint', event.target.checked)} />
                    <Toggle label={t('ttsInstructions')} info={t('ttsInstructionsInfo')} checked={!!actions.instructions} onChange={(event) => this.setAction('instructions', event.target.checked)} />
                    <Toggle label={t('ttsDeliveryDay')} info={t('ttsDeliveryDayInfo')} checked={!!actions.deliveryDay} onChange={(event) => this.setAction('deliveryDay', event.target.checked)} />
                    {actions.deliveryDay &&
                        <Input
                            type='number'
                            label={t('ttsDeliveryDayCount')}
                            value={settings.deliveryDayCount || DEFAULT_DELIVERY_DAY_COUNT}
                            onChange={(event) => this.set('orders.trackTrace.deliveryDayCount', Math.min(30, Math.max(1, parseInt(event.target.value) || DEFAULT_DELIVERY_DAY_COUNT)), false)}
                            onBlur={() => this.save('orders.trackTrace.deliveryDayCount')}
                        />
                    }
                </FormStack>

                {actions.safePlace &&
                    <>
                        <Divider />
                        <P>{t('ttsSafePlaces')}</P>
                        <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                            {options.map((option) => (
                                <Badge key={option} style={{gap: 3}}>
                                    {option}
                                    <IconButton
                                        icon='mdi mdi-close'
                                        aria-label={t('ttsRemoveItem', option)}
                                        disabled={options.length === 1}
                                        onClick={() => this.set('orders.trackTrace.safePlaceOptions', options.filter((item) => item !== option))}
                                    />
                                </Badge>
                            ))}
                        </div>
                        <form
                            style={{display: 'flex', gap: 12, alignItems: 'flex-end'}}
                            onSubmit={(event) => {
                                event.preventDefault()
                                this.addSafePlace()
                            }}
                        >
                            <Input style={{flex: 1, maxWidth: 320}} label={t('ttsAddPlace')} placeholder={t('ttsAddPlacePlaceholder')} value={this.state.newSafePlace} onChange={(event) => this.setState({newSafePlace: event.target.value.slice(0, 60)})} />
                            <Button type='submit' variant='outline' label={t('ttsAdd')} />
                        </form>
                        <S2>{t('ttsOtherPlaceInfo')}</S2>
                    </>
                }

                {actions.pickupPoint &&
                    <>
                        <Divider />
                        <P>{t('ttsPickupPoints')}</P>
                        {!pickupPoints.length && <Alert variant='warning'>{t('ttsNoPickupPoints')}</Alert>}

                        {pickupPoints.map((point, index) => (
                            <Card key={point.id} style={{display: 'flex', flexDirection: 'column', padding: 12}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <S2>{t('ttsPickupPointNr', index + 1)}</S2>
                                    <IconButton icon='mdi mdi-delete' aria-label={t('ttsRemoveItem', point.name || t('ttsPickupPointNr', index + 1))} onClick={() => this.set('orders.trackTrace.pickupPoints', pickupPoints.filter((_item, i) => i !== index))} />
                                </div>
                                <FormStack>
                                    <Input label={t('ttsPickupPointName')} value={point.name} onChange={(event) => this.onChangePickupPoint(index, 'name', event.target.value.slice(0, 60))} onBlur={() => this.save('orders.trackTrace.pickupPoints')} />
                                    <Input label={t('ttsPickupPointAddress')} value={point.address} onChange={(event) => this.onChangePickupPoint(index, 'address', event.target.value.slice(0, 120))} onBlur={() => this.save('orders.trackTrace.pickupPoints')} />
                                    <TextArea label={t('ttsOpeningHours')} rows={2} value={point.openingHours} onChange={(event) => this.onChangePickupPoint(index, 'openingHours', event.target.value.slice(0, 200))} onBlur={() => this.save('orders.trackTrace.pickupPoints')} />
                                </FormStack>
                            </Card>
                        ))}

                        <Button style={{alignSelf: 'flex-start'}} variant='outline' icon='mdi mdi-plus' label={t('ttsAddPickupPoint')} onClick={() => this.addPickupPoint()} />

                        <FormStack>
                            <Input
                                type='number'
                                label={t('ttsPickupPeriod')}
                                info={t('ttsPickupPeriodInfo')}
                                value={settings.pickupPeriod || DEFAULT_PICKUP_PERIOD}
                                onChange={(event) => this.set('orders.trackTrace.pickupPeriod', Math.min(30, Math.max(1, parseInt(event.target.value) || DEFAULT_PICKUP_PERIOD)), false)}
                                onBlur={() => this.save('orders.trackTrace.pickupPeriod')}
                            />
                        </FormStack>
                    </>
                }
            </>
        )
    }

    renderFuncties() {
        const {reseller} = this.props
        const settings = this.settings()
        const photoUrls = trackTraceBranding({_id: reseller._id, settings: {...reseller.settings, orders: {trackTrace: {...settings, showPhoto: true}}}}).photo

        return (
            <>
                {this.renderActions()}

                <Divider />

                <H5>{t('ttsDisplay')}</H5>
                <FormStack>
                    <Select
                        label={t('ttsTimeFrame')}
                        info={t('ttsTimeFrameInfo')}
                        value={settings.timeFrame || 60}
                        options={[15, 30, 45, 60, 120, 240].map((minutes) => ({value: minutes, title: minutes < 60 ? t('ttsMinutes', minutes) : t('ttsHours', minutes / 60)}))}
                        onChange={(event) => this.set('orders.trackTrace.timeFrame', parseInt(event.target.value))}
                    />
                    <Toggle label={t('ttsHidePhoto')} info={t('ttsHidePhotoInfo')} checked={!!settings.hidePhoto} onChange={(event) => this.set('orders.trackTrace.hidePhoto', event.target.checked)} />
                    <Select
                        label={t('ttsStatusIcon')}
                        info={t('ttsStatusIconInfo')}
                        value={settings.outForDeliveryStatusIcon || 'bike'}
                        options={[{value: 'bike', title: t('ttsBike')}, {value: 'cargo-bike', title: t('ttsCargoBike')}, {value: 'car', title: t('ttsCar')}]}
                        onChange={(event) => this.set('orders.trackTrace.outForDeliveryStatusIcon', event.target.value)}
                    />
                </FormStack>

                <Divider />

                <H5>{t('ttsOwnContent')}</H5>
                <S2>{t('ttsOwnContentInfo')}</S2>
                <FormStack>
                    <Select
                        label={t('ttsMap')}
                        info={t('ttsMapInfo')}
                        value={settings.map || 'none'}
                        options={[
                            {value: 'none', title: t('ttsMapNone')},
                            {value: 'recipient', title: t('ttsMapRecipient')},
                            {value: 'recipientCourier', title: t('ttsMapCourier')},
                            {value: 'recipientCourierStops', title: t('ttsMapCourierStops')}
                        ]}
                        onChange={(event) => this.set('orders.trackTrace.map', event.target.value)}
                    />

                    <Toggle label={t('ttsShowPhoto')} checked={!!settings.showPhoto} onChange={(event) => this.set('orders.trackTrace.showPhoto', event.target.checked)} />
                    {settings.showPhoto && ['desktop', 'mobile'].map((kind) => (
                        <React.Fragment key={kind}>
                            <UploadImage
                                label={t(kind === 'desktop' ? 'ttsPhotoDesktop' : 'ttsPhotoMobile')}
                                subText={t('ttsPhotoSize', `${trackTracePhotoSizes[kind].width} × ${trackTracePhotoSizes[kind].height}`)}
                                image={photoUrls?.[kind] || ''}
                                accept='image/png,image/jpeg'
                                resize={false}
                                maxSize={10}
                                onChange={(dataUrl) => this.onChangePhoto(kind, dataUrl)}
                            />
                            {this.state.photoError[kind] && <Alert variant='danger'>{this.state.photoError[kind]}</Alert>}
                        </React.Fragment>
                    ))}

                    <Toggle label={t('ttsShowMessage')} checked={!!settings.showRecipientMessage} onChange={(event) => this.set('orders.trackTrace.showRecipientMessage', event.target.checked)} />
                    {settings.showRecipientMessage &&
                        <Input
                            label={t('ttsRecipientMessage')}
                            info={t('ttsRecipientMessageInfo', RECIPIENT_MESSAGE_MAX)}
                            value={settings.recipientMessage || ''}
                            onChange={(event) => this.set('orders.trackTrace.recipientMessage', event.target.value.slice(0, RECIPIENT_MESSAGE_MAX), false)}
                            onBlur={() => this.save('orders.trackTrace.recipientMessage')}
                        />
                    }
                </FormStack>
            </>
        )
    }

    renderTeksten() {
        const {reseller} = this.props
        const {language} = this.state
        const settings = this.settings()
        const defaults = defaultMessages(language)
        const faq = this.faq()

        return (
            <>
                <H5>{t('ttsMessagesTitle')}</H5>
                <Tabs value={language} onChange={(event) => this.setState({language: event.target.value})}>
                    {trackTraceLanguages.map((code) => <Tab key={code} value={code}>{code.toUpperCase()}</Tab>)}
                </Tabs>
                <S2>{t('ttsMessagesInfo')}</S2>

                {messageGroups.map((group) => (
                    <React.Fragment key={group.title}>
                        <P>{t(group.title)}</P>
                        <FormStack>
                            {group.fields.map(({key, label}) => {
                                const path = this.messagePath(key)

                                return (
                                    <TextArea
                                        key={`${language}-${key}`}
                                        label={t(label)}
                                        info={t('ttsDefaultText', defaults[key])}
                                        rows={2}
                                        value={_.get(reseller.settings, path) || ''}
                                        onChange={(event) => this.set(path, event.target.value, false)}
                                        onBlur={() => this.save(path)}
                                    />
                                )
                            })}
                        </FormStack>
                    </React.Fragment>
                ))}

                <Divider />

                <H5>{t('ttsFaq')}</H5>
                <FormStack>
                    <Toggle label={t('ttsShowFaq')} info={t('ttsShowFaqInfo')} checked={!!settings.showFaq} onChange={(event) => this.set('orders.trackTrace.showFaq', event.target.checked)} />
                </FormStack>

                {settings.showFaq &&
                    <>
                        {faq.map((item, index) => (
                            <Card key={index} style={{display: 'flex', flexDirection: 'column', padding: 12}}>
                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                    <S2>{t('ttsQuestionNr', index + 1)}</S2>
                                    <IconButton icon='mdi mdi-delete' aria-label={t('ttsRemoveItem', t('ttsQuestionNr', index + 1))} onClick={() => this.set('orders.trackTrace.faq', faq.filter((_item, i) => i !== index))} />
                                </div>
                                <FormStack>
                                    <Input label={t('ttsQuestion')} value={item.question || ''} onChange={(event) => this.onChangeFaq(index, 'question', event.target.value)} onBlur={() => this.save('orders.trackTrace.faq')} />
                                    <TextArea label={t('ttsAnswer')} rows={2} value={item.answer || ''} onChange={(event) => this.onChangeFaq(index, 'answer', event.target.value)} onBlur={() => this.save('orders.trackTrace.faq')} />
                                </FormStack>
                            </Card>
                        ))}

                        <Button style={{alignSelf: 'flex-start'}} variant='outline' icon='mdi mdi-plus' label={t('ttsAddQuestion')} onClick={() => this.set('orders.trackTrace.faq', [...faq, {question: '', answer: ''}], false)} />
                    </>
                }
            </>
        )
    }

    renderTalen() {
        const settings = this.settings()
        const enabled = this.enabledLanguages()

        return (
            <>
                <H5>{t('ttsLanguages')}</H5>
                <S2>{t('ttsLanguagesInfo')}</S2>
                <FormStack>
                    {trackTraceLanguages.map((code) => (
                        <Toggle
                            key={code}
                            label={languageNames[code]}
                            info={t(code === 'nl' ? 'ttsLanguageDutchInfo' : 'ttsLanguageOtherInfo')}
                            checked={enabled.includes(code)}
                            onChange={(event) => this.onToggleLanguage(code, event.target.checked)}
                        />
                    ))}
                    <Select
                        label={t('ttsDefaultLanguage')}
                        value={settings.defaultLanguage || 'nl'}
                        options={enabled.map((code) => ({value: code, title: languageNames[code]}))}
                        onChange={(event) => this.set('orders.trackTrace.defaultLanguage', event.target.value)}
                    />
                </FormStack>
            </>
        )
    }

    // The preview only remembers the recipient's choice locally
    previewAction(request) {
        const {previewSample} = this.state
        const settings = this.settings()

        if (request.type === 'safePlace') {
            this.setState({previewSample: {...previewSample, choice: {type: 'safePlace', place: request.place, note: request.note || ''}}})
        } else if (request.type === 'neighbour') {
            this.setState({previewSample: {...previewSample, choice: {type: 'neighbour', nr: request.nr}}})
        } else if (request.type === 'pickupPoint') {
            const pickupPoint = (settings.pickupPoints || []).find((point) => point.id === request.id)
            this.setState({previewState: 'onHold', previewSample: {...previewSample, choice: {type: 'pickupPoint', pickupPoint, until: sampleView(this.props.reseller, 'scheduled', 'nl').details.actions?.pickupUntil || ''}}})
        } else if (request.type === 'deliveryDay') {
            this.setState({previewSample: {...previewSample, choice: {type: 'deliveryDay', date: request.date}}})
        } else if (request.type === 'instructions') {
            this.setState({previewSample: {...previewSample, instructionsText: request.text}})
        } else {
            this.setState({previewSample: {...previewSample, choice: null}})
        }

        return null
    }

    renderPreview() {
        const {reseller} = this.props
        const {language, previewDevice, previewState, previewSample, previewZoom} = this.state

        const view = sampleView(reseller, previewState, language, previewSample)

        const page = (
            <TrackTracePage
                preview
                view={view}
                language={language}
                onLanguageChange={(code) => this.setState({language: code})}
                onAction={async (request) => this.previewAction(request)}
            />
        )

        return (
            <Card shadow style={{...sectionStyle, flex: '1 1 420px', position: 'sticky', top: 12, alignSelf: 'flex-start', minWidth: 0}}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12}}>
                    <H5>{t('ttsPreview')}</H5>
                    <Tabs value={previewDevice} onChange={(event) => this.setState({previewDevice: event.target.value})}>
                        <Tab value='mobile'>{t('ttsMobile')}</Tab>
                        <Tab value='desktop'>{t('ttsDesktop')}</Tab>
                    </Tabs>
                </div>

                <div style={{display: 'flex', flexWrap: 'wrap', gap: 6}}>
                    {sampleStates.map(({state, label}) => (
                        <Button
                            key={state}
                            variant='outline'
                            selected={previewState === state}
                            label={t(label)}
                            onClick={() => this.setState({previewState: state, previewSample: {choice: null, instructionsText: ''}})}
                        />
                    ))}
                </div>

                {previewDevice === 'mobile' ?
                    <div style={{alignSelf: 'center', width: 340, height: 680, border: '6px solid #242424', borderRadius: 24, overflow: 'auto'}}>
                        {page}
                    </div> :
                    <div ref={this.desktopPreview} style={{height: 680, overflowY: 'auto', overflowX: 'hidden', border: '1px solid #BEBEBE', borderRadius: 4}}>
                        <div style={{width: DESKTOP_PREVIEW_WIDTH, zoom: previewZoom}}>{page}</div>
                    </div>
                }
            </Card>
        )
    }

    render() {
        const {tab} = this.state

        return (
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start'}}>
                <Card shadow style={{...sectionStyle, flex: '1 1 520px', maxWidth: 680, minWidth: 0}}>
                    <S2>{t('ttsBreadcrumb')}</S2>
                    <H3>{t('ttsTitle')}</H3>
                    <P>{t('ttsIntro')}</P>

                    <FormStack>
                        {this.renderSwitchover()}
                    </FormStack>

                    <Tabs value={tab} onChange={(event) => this.setState({tab: event.target.value})}>
                        <Tab value='huisstijl'>{t('ttsTabStyle')}</Tab>
                        <Tab value='functies'>{t('ttsTabFeatures')}</Tab>
                        <Tab value='teksten'>{t('ttsTabTexts')}</Tab>
                        <Tab value='talen'>{t('ttsTabLanguages')}</Tab>
                    </Tabs>

                    {tab === 'huisstijl' && this.renderHuisstijl()}
                    {tab === 'functies' && this.renderFuncties()}
                    {tab === 'teksten' && this.renderTeksten()}
                    {tab === 'talen' && this.renderTalen()}
                </Card>

                {this.renderPreview()}
            </div>
        )
    }
}

export default TrackTrace
